/**
 * AstorBriefingCard — non-dismissible Astor banner on the loan detail page.
 *
 * "I've pre-staged 4 documents based on your last submission. Want to review
 * before they go to the lender?" + actions (Review & send / See changes).
 *
 * Mint-bordered Box with pulsing Astor avatar. Uses concierge voice; all copy
 * comes from i18n keys passed via props.
 */

'use client';

import { Box, baseColors, GlassButton, Stack, Typography } from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';
import { AstorAvatar } from './AstorAvatar';

export interface AstorBriefingCardProps {
  headline: string;
  body: string;
  primaryLabel: string;
  secondaryLabel?: string;
  onPrimary?: () => void;
  onSecondary?: () => void;
}

export function AstorBriefingCard({
  headline,
  body,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
}: AstorBriefingCardProps): ReactElement {
  return (
    <Box
      sx={{
        p: 2.25,
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${baseColors.teal[200]}`,
        bgcolor: 'background.paper',
        background: `linear-gradient(135deg, ${baseColors.teal[50]} 0%, transparent 70%)`,
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      <AstorAvatar size={28} pulse />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
          {headline}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {body}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <GlassButton variant="gradient" size="small" onClick={onPrimary}>
            {primaryLabel}
          </GlassButton>
          {secondaryLabel && (
            <GlassButton variant="text" size="small" onClick={onSecondary}>
              {secondaryLabel}
            </GlassButton>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
